// PAYE maths, kept apart from the page so the sample calculator and the save
// path provably use the same numbers.
//
// TWO SHAPES, and the difference is the whole reason this file exists:
//
//   BANDS - how KRA publishes the schedule, and how a person thinks:
//           "first 24,000 at 10%, next 8,333 at 25%, ... above 800,000 at 35%".
//           Each band carries ITS OWN rate. The last band has no width.
//
//   ROWS  - how Tax_Master stores it, and what the payroll engine reads.
//           One row per THRESHOLD, carrying the rate that applies ABOVE that
//           threshold (i.e. the NEXT band's rate) plus the cumulative tax due
//           at it. N bands become N-1 rows.
//
// The old page made the operator enter ROWS by hand, which meant shifting
// every rate up one line. That off-by-one is what this converts away.

/** Statutory rates used by the sample calculator's defaults. */
export const NSSF_RATE = 6;
export const NSSF_MIN = 540;
export const NSSF_MAX = 6480;
export const SHIF_RATE = 2.75;
export const HOUSING_LEVY_RATE = 1.5;

const num = (v) => Number(v) || 0;

/** Half away from zero, matching Math.Round(..., MidpointRounding.AwayFromZero) in payroll. */
export const roundAway = (v, dp = 0) => {
  const f = 10 ** dp;
  return (Math.sign(v) || 1) * Math.round(Math.abs(v) * f + Number.EPSILON) / f;
};

/** BANDS -> stored ROWS. Returns one row per threshold; the top band contributes only its rate. */
export const bandsToRows = (bands) => {
  const rows = [];
  let threshold = 0;
  let cumulativeTax = 0;

  for (let i = 0; i < bands.length - 1; i += 1) {
    const width = num(bands[i].width);
    threshold += width;
    // Tax accrued across THIS band, at this band's own rate.
    cumulativeTax += (width * num(bands[i].rate)) / 100;
    rows.push({
      slNo: bands[i].slNo ?? null,
      taxFormulaAmount: width,
      taxAmount: threshold,
      // The rate above this threshold is the NEXT band's rate.
      deductionPercentage: num(bands[i + 1].rate),
      dedAmount: roundAway(cumulativeTax, 2),
    });
  }
  return rows;
};

/**
 * Stored ROWS -> BANDS, the inverse of the above.
 * The first band's own rate is stored nowhere, so it is recovered from the
 * first row: cumulative tax at the first threshold / that threshold.
 */
export const rowsToBands = (rows) => {
  if (!rows.length) return [{ slNo: null, width: "", rate: "" }];
  const sorted = [...rows].sort((a, b) => num(a.taxAmount) - num(b.taxAmount));

  const firstThreshold = num(sorted[0].taxAmount);
  const firstRate = firstThreshold ? (num(sorted[0].dedAmount) / firstThreshold) * 100 : 0;

  const bands = sorted.map((r, i) => ({
    slNo: r.slNo ?? null,
    width: String(num(r.taxFormulaAmount)),
    rate: String(i === 0 ? roundAway(firstRate, 2) : num(sorted[i - 1].deductionPercentage)),
  }));

  // Top band: rate only, taken from the last row's "rate above".
  bands.push({ slNo: null, width: "", rate: String(num(sorted[sorted.length - 1].deductionPercentage)) });
  return bands;
};

/**
 * The payroll engine's PAYE lookup, reproduced exactly
 * (Texas_Admin\BL\Common_BL.cs getTaxdeduction).
 *
 * It is NOT a walk over the bands: it picks the single row with the largest
 * threshold strictly below the taxable base, then
 *     tax = (base - threshold) * rateAbove/100 + cumulativeTax - relief
 * Below the lowest threshold NO row matches and the result is zero - which is
 * only correct while band 1's tax happens to equal the relief. The quirk is
 * reproduced deliberately so the preview cannot flatter the real thing.
 */
export const computePaye = (base, rows, relief) => {
  const candidates = rows.filter((r) => num(r.taxAmount) < base).sort((a, b) => num(b.taxAmount) - num(a.taxAmount));
  if (candidates.length === 0) return { matched: null, gross: 0, paye: 0 };

  const m = candidates[0];
  const grossTax = ((base - num(m.taxAmount)) * num(m.deductionPercentage)) / 100 + num(m.dedAmount);
  const paye = Math.max(0, roundAway(grossTax - num(relief), 2));
  return { matched: m, gross: grossTax, paye: roundAway(paye, 0) };
};

/** Default statutory deductions for a sample gross - all overridable on screen. */
export const defaultStatutory = (gross) => {
  const g = num(gross);
  const nssf = Math.min(Math.max(roundAway((g * NSSF_RATE) / 100), NSSF_MIN), NSSF_MAX);
  return {
    nssf: g > 0 ? nssf : 0,
    shif: roundAway((g * SHIF_RATE) / 100),
    levy: roundAway((g * HOUSING_LEVY_RATE) / 100),
  };
};

/** Per-band contribution for the sample breakdown - presentation only. */
export const explainBands = (base, bands) => {
  let lower = 0;
  let remaining = base;
  const out = [];

  for (let i = 0; i < bands.length; i += 1) {
    if (remaining <= 0) break;
    const isTop = i === bands.length - 1;
    const width = isTop ? Infinity : num(bands[i].width);
    const taxed = Math.min(remaining, width);
    const upper = isTop ? null : lower + width;
    out.push({
      label: isTop ? `Above ${lower.toLocaleString()}` : `${lower.toLocaleString()} - ${upper.toLocaleString()}`,
      rate: num(bands[i].rate),
      taxed,
      tax: (taxed * num(bands[i].rate)) / 100,
    });
    remaining -= taxed;
    lower = upper ?? lower;
  }
  return out;
};
